import { useFeatureFlagStore } from '@moniebook-web/store';
import { type Unsubscribe } from 'firebase/firestore';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessStore, useRegisterConfiguration } from '../store';
import { FEATURE_FLAGS } from '../types';
import { firestoreChangeTracker } from './init';
import { firestoreRegisterEventTracker } from './register-event-tracker';
import { firestoreStockEventTracker } from './stock-event-tracker';
import { useAuthState } from '../hooks';

export function FirestoreSyncManager() {
    const { config } = useRegisterConfiguration();
    const { businessInfo } = useBusinessStore();
    const { featureFlags } = useFeatureFlagStore();
    const { hasBranchAuthToken } = useAuthState();
    const navigate = useNavigate();

    const businessId = businessInfo?.businessId;
    const branchId = businessInfo?.branchId;
    const enableNewStockTracking =
        featureFlags?.[FEATURE_FLAGS.ENABLE_NEW_FIRESTORE_STOCK_TRACKING] ??
        false;
    const enableChangeTrackerThrottle =
        featureFlags?.[FEATURE_FLAGS.ENABLE_CHANGE_TRACKER_THROTTLE] ?? false;

    useEffect(() => {
        if (!businessId || !hasBranchAuthToken) {
            return;
        }

        return firestoreChangeTracker.subscribe(businessId, {
            throttle: enableChangeTrackerThrottle,
        });
    }, [businessId, hasBranchAuthToken, enableChangeTrackerThrottle]);

    useEffect(() => {
        if (!config.registerId || !branchId) {
            return;
        }

        let unsubscribeStockTracker: Unsubscribe | undefined;

        if (enableNewStockTracking) {
            unsubscribeStockTracker =
                firestoreStockEventTracker.subscribe(branchId);
        }

        const unsubscribeRegisterTracker =
            firestoreRegisterEventTracker.subscribe(
                config.registerId,
                navigate,
                enableNewStockTracking,
            );

        return () => {
            unsubscribeStockTracker?.();
            unsubscribeRegisterTracker();
        };
    }, [
        businessId,
        navigate,
        config.registerId,
        branchId,
        enableNewStockTracking,
    ]);

    return null;
}
